import { CSSProperties, useContext, useEffect, useMemo, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { AlertComponent, DialogComponent } from "../../components";
import { useDate, useFetch, usePermissions } from "../../hooks";
import { ClientProps, TechListProps, WellProps } from "../../interfaces/interfaces";
import { Link, useNavigate } from "react-router-dom";
import DataContext from "../../context/DataContext";
import PaginatorContext from "../../context/PaginatorContext";
import AuthContext from "../../context/AuthContext";

// ── Design tokens (identical to DocumentsPortalLayout) ──
const OG = "#ED8B00";
const CARD = "#fff";
const PAGE_BG = "#F3F3F0";
const BORDER = "#E7E7E3";
const BORDER_LIGHT = "#EFEFEC";
const BORDER_GRID = "#F2F2EF";
const TEXT = "#2A2F38";
const TEXT_MID = "#5A6170";
const TEXT_MUTED = "#8A8F99";
const TEXT_FAINT = "#9CA1AB";
const SEARCH_BG = "#FAFAF8";

type DateRange = "all" | "7" | "30" | "90" | "ytd" | "custom";

const computeDateRange = (
  range: DateRange, from: string, to: string,
): { dateFrom?: string; dateTo?: string } => {
  if (range === "all") return {};
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  if (range === "7") { const f = new Date(today); f.setDate(f.getDate() - 7); return { dateFrom: fmt(f) }; }
  if (range === "30") { const f = new Date(today); f.setDate(f.getDate() - 30); return { dateFrom: fmt(f) }; }
  if (range === "90") { const f = new Date(today); f.setDate(f.getDate() - 90); return { dateFrom: fmt(f) }; }
  if (range === "ytd") return { dateFrom: `${today.getFullYear()}-01-01` };
  return { dateFrom: from || undefined, dateTo: to || undefined };
};

const fmtDate = (d: string) => {
  if (!d) return "—";
  const dt = new Date(d.includes("T") ? d : d + "T00:00:00");
  if (isNaN(dt.getTime())) return "—";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

interface FetchResponse { success?: boolean; data: TechListProps[]; }

const lbl: CSSProperties = {
  fontSize: 10.5, fontWeight: 600, letterSpacing: ".6px",
  textTransform: "uppercase", color: TEXT_FAINT, display: "block", marginBottom: 5,
};
const selSt: CSSProperties = {
  padding: "8px 11px", border: "1px solid #E2E2DD", borderRadius: 9,
  fontSize: 13, background: SEARCH_BG, color: TEXT, outline: "none",
  cursor: "pointer", fontFamily: "inherit", width: "100%",
};
const inpSt: CSSProperties = {
  width: "100%", padding: "9px 12px 9px 34px", border: "1px solid #E2E2DD",
  borderRadius: 9, fontSize: 13, outline: "none", background: SEARCH_BG,
  color: TEXT, fontFamily: "inherit", boxSizing: "border-box",
};
const dateInpSt: CSSProperties = {
  padding: "7px 10px", border: "1px solid #E2E2DD", borderRadius: 8,
  fontSize: 13, background: SEARCH_BG, outline: "none", fontFamily: "inherit",
};
const navBtnSt: CSSProperties = {
  width: 30, height: 30, borderRadius: 8, border: "1px solid #E2E2DD",
  background: CARD, display: "inline-flex", alignItems: "center",
  justifyContent: "center", cursor: "pointer", color: "#4A5160", padding: 0,
};
const navBtnOffSt: CSSProperties = {
  ...navBtnSt, color: "#CFD2D8", cursor: "not-allowed", opacity: 0.5,
};
const gridCols = "1.2fr 1fr 1.2fr 100px 130px 200px";

export const TechDesignList = () => {
  const navigate = useNavigate();
  const { isDrawerOpen } = useContext(AuthContext);
  const drawerWidth = isDrawerOpen ? 250 : 64;
  const { paginationModel, fetchPaginationModel } = useContext(PaginatorContext);
  const {
    onResetValues, fetchDataTechProp, onDeleteProp,
    isSuccess, isError, clientOptions, wellOptions,
    fetchDataWells, fetchDataClient,
  } = useContext(DataContext);
  const { getDateFromString } = useDate();
  const { canManageDesigns } = usePermissions();

  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [tableData, setTableData] = useState<TechListProps[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(null);
  const [selectedWell, setSelectedWell] = useState<WellProps | null>(null);
  const [createdBy, setCreatedBy] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [wellSearch, setWellSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [isClientOpen, setIsClientOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    fetchDataClient();
    const token = localStorage.getItem("info");
    fetch(`${process.env.REACT_APP_DEV_API}/user`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((r) => { if (r.success && r.data) setUserOptions(r.data.filter((u: any) => u.active && u.name)); })
      .catch(() => {});
  }, []);

  const filters = useMemo(() => {
    const { dateFrom, dateTo } = computeDateRange(dateRange, customFrom, customTo);
    return {
      search: searchTerm, client: selectedClient?.id, well: selectedWell?.id,
      createdBy: createdBy || undefined, dateFrom, dateTo,
    };
  }, [searchTerm, selectedClient, selectedWell, createdBy, dateRange, customFrom, customTo]);

  const { data, isLoading, fetchData, pagination } = useFetch<FetchResponse>(
    "proposal-detail", paginationModel, undefined, undefined, filters,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  }, [searchTerm, selectedClient, selectedWell, createdBy, dateRange, customFrom, customTo]);

  useEffect(() => { data?.data && setTableData(data.data); }, [data]);

  const filteredWells = wellSearch.trim()
    ? (wellOptions || []).filter((w) => w.name.toLowerCase().includes(wellSearch.toLowerCase()))
    : (wellOptions || []);

  const filteredClients = clientSearch.trim()
    ? clientOptions.filter((c) => c.name.toLowerCase().includes(clientSearch.toLowerCase()))
    : clientOptions;

  const total = +(pagination?.totalRecords ?? 0);
  const curPage = paginationModel.page;
  const pageSize = paginationModel.pageSize;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = total === 0 ? 0 : curPage * pageSize + 1;
  const pageEnd = Math.min((curPage + 1) * pageSize, total);

  return (
    <>
      <Box sx={{
        position: "fixed", top: 0, left: `${drawerWidth}px`, right: 0, bottom: 0,
        display: "flex", flexDirection: "column", background: PAGE_BG,
        fontFamily: "'Public Sans', Helvetica, Arial, sans-serif", fontSize: 14, color: TEXT,
        transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)", zIndex: 1,
      }}>

        {/* ── Header ── */}
        <Box component="header" sx={{
          height: 66, flexShrink: 0, background: CARD, borderBottom: `1px solid ${BORDER}`,
          display: "flex", alignItems: "center", px: "26px", gap: 2,
        }}>
          <Box sx={{ lineHeight: 1.15 }}>
            <Box sx={{ fontSize: 19, fontWeight: 700, color: TEXT }}>Technical Proposals</Box>
            <Box sx={{ fontSize: 12, color: TEXT_MUTED }}>Browse, filter and manage technical proposals</Box>
          </Box>
          {canManageDesigns && (
            <Box component="button" onClick={() => { onResetValues(); navigate("/tech/0"); }}
              sx={{
                ml: "auto", display: "inline-flex", alignItems: "center", gap: "7px",
                background: OG, color: "#fff", border: "none", borderRadius: "9px",
                px: "15px", py: "9px", fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit", "&:hover": { background: "#D97A00" },
              }}>
              + New
            </Box>
          )}
        </Box>

        {/* ── Workspace ── */}
        <Box component="main" sx={{
          flex: 1, display: "grid", gridTemplateColumns: "316px 1fr",
          gap: "22px", p: "22px 26px", minHeight: 0, boxSizing: "border-box",
        }}>

          {/* ── Wells panel ── */}
          <Box sx={{
            display: "flex", flexDirection: "column", minHeight: 0,
            background: CARD, border: `1px solid ${BORDER}`,
            borderRadius: "14px", overflow: "hidden",
          }}>
            {/* Client autocomplete */}
            <Box sx={{ p: "12px 18px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
              <label style={lbl}>Client</label>
              <Box sx={{ position: "relative" }}>
                <Box sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A6ABB5", display: "flex", pointerEvents: "none" }}>
                  <SearchSvg />
                </Box>
                <input
                  value={isClientOpen ? clientSearch : (selectedClient?.name ?? "")}
                  placeholder={!selectedClient ? "Search clients…" : ""}
                  onFocus={() => { setClientSearch(""); setIsClientOpen(true); }}
                  onChange={(e) => setClientSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setIsClientOpen(false), 150)}
                  style={inpSt}
                />
                {isClientOpen && (
                  <Box sx={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
                    background: CARD, border: `1px solid ${BORDER}`, borderRadius: "9px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.10)", maxHeight: 200, overflowY: "auto",
                  }}>
                    {selectedClient && (
                      <Box
                        onMouseDown={(e) => { e.preventDefault(); setSelectedClient(null); setSelectedWell(null); setClientSearch(""); setIsClientOpen(false); }}
                        sx={{ py: "8px", px: "13px", fontSize: 13, color: TEXT_MUTED, cursor: "pointer", borderBottom: `1px solid ${BORDER_LIGHT}`, "&:hover": { background: "#F7F7F4" } }}
                      >
                        Clear selection
                      </Box>
                    )}
                    {filteredClients.map((c) => (
                      <Box key={c.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedClient(c); setSelectedWell(null);
                          fetchDataWells(c.id); setClientSearch(""); setIsClientOpen(false);
                        }}
                        sx={{
                          py: "8px", px: "13px", fontSize: 13, color: TEXT, cursor: "pointer",
                          background: selectedClient?.id === c.id ? "#FDF3E3" : "transparent",
                          "&:hover": { background: "#F7F7F4" },
                        }}
                      >
                        {c.name}
                      </Box>
                    ))}
                    {filteredClients.length === 0 && (
                      <Box sx={{ py: "10px", px: "13px", fontSize: 13, color: TEXT_FAINT }}>
                        No clients match "{clientSearch}"
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Wells heading + search */}
            <Box sx={{ p: "16px 18px 12px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "12px" }}>
                <Box sx={{ fontSize: 13, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "#4A5160" }}>Wells</Box>
                <Box sx={{ background: "#F1F1ED", color: "#7A808C", fontSize: 11, fontWeight: 600, px: "9px", py: "2px", borderRadius: "20px" }}>
                  {(wellOptions || []).length}
                </Box>
              </Box>
              <Box sx={{ position: "relative" }}>
                <Box sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A6ABB5", display: "flex", pointerEvents: "none" }}>
                  <SearchSvg />
                </Box>
                <input value={wellSearch} onChange={(e) => setWellSearch(e.target.value)} placeholder="Search wells…" style={inpSt} />
              </Box>
            </Box>

            {/* Well list */}
            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {!selectedClient && (
                <Box sx={{ p: "28px 18px", textAlign: "center", color: TEXT_FAINT, fontSize: 13 }}>
                  Select a client to see their wells.
                </Box>
              )}
              {selectedClient && filteredWells.length === 0 && (
                <Box sx={{ p: "28px 18px", textAlign: "center", color: TEXT_FAINT, fontSize: 13 }}>
                  {wellSearch ? `No wells match "${wellSearch}".` : "No wells found."}
                </Box>
              )}
              {filteredWells.map((w) => {
                const active = w.id === selectedWell?.id;
                return (
                  <Box key={w.id} onClick={() => setSelectedWell(active ? null : w)}
                    sx={{
                      display: "flex", alignItems: "center", gap: "11px",
                      py: "9px", px: "16px", cursor: "pointer",
                      borderBottom: `1px solid ${BORDER_GRID}`,
                      background: active ? "#FDF3E3" : CARD,
                      "&:hover": { background: active ? "#FDF3E3" : "#F7F7F4" },
                      fontSize: 13.5,
                    }}>
                    <Box sx={{ width: 3, height: 18, borderRadius: "3px", flexShrink: 0, background: active ? OG : "transparent" }} />
                    <Box sx={{ color: active ? "#A8650A" : "#3A4150", fontWeight: active ? 700 : 500, letterSpacing: ".2px", flex: 1 }}>
                      {w.name}
                    </Box>
                    {active && <Box sx={{ color: OG, display: "flex" }}><CheckSvg /></Box>}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ p: "11px 18px", borderTop: `1px solid ${BORDER_LIGHT}`, fontSize: 12, color: TEXT_MUTED }}>
              Showing {filteredWells.length} of {(wellOptions || []).length} wells
            </Box>
          </Box>

          {/* ── Designs panel ── */}
          <Box sx={{
            display: "flex", flexDirection: "column", minHeight: 0,
            background: CARD, border: `1px solid ${BORDER}`,
            borderRadius: "14px", overflow: "hidden",
          }}>
            {/* Toolbar */}
            <Box sx={{ p: "16px 20px", borderBottom: `1px solid ${BORDER_LIGHT}`, display: "flex", flexDirection: "column", gap: "13px", flexShrink: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Box sx={{ fontSize: 15, fontWeight: 700, color: TEXT }}>{total} Technical Proposals</Box>
                {selectedWell && (
                  <Box sx={{
                    display: "inline-flex", alignItems: "center", gap: "7px",
                    background: "#FDF1DE", color: "#B5710A", border: "1px solid #F2D7A8",
                    fontSize: 12, fontWeight: 600, py: "4px", pl: "11px", pr: "7px", borderRadius: "20px",
                  }}>
                    Well: {selectedWell.name}
                    <Box component="span" onClick={() => setSelectedWell(null)}
                      sx={{ cursor: "pointer", display: "flex", color: "#C5893A", "&:hover": { color: "#8a5500" } }}>
                      <CloseSvg />
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-end", gap: "11px", flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 180, position: "relative" }}>
                  <Box sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A6ABB5", display: "flex", pointerEvents: "none" }}>
                    <SearchSvg />
                  </Box>
                  <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search proposals…" style={inpSt} />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <label style={lbl}>Date range</label>
                  <select value={dateRange} onChange={(e) => setDateRange(e.target.value as DateRange)} style={{ ...selSt, width: "auto" }}>
                    <option value="all">All time</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="ytd">This year</option>
                    <option value="custom">Custom range</option>
                  </select>
                </Box>

                {userOptions.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <label style={lbl}>Created by</label>
                    <select value={createdBy ?? "all"} onChange={(e) => setCreatedBy(e.target.value === "all" ? null : e.target.value)} style={{ ...selSt, width: "auto" }}>
                      <option value="all">All creators</option>
                      {userOptions.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                    </select>
                  </Box>
                )}
              </Box>

              {dateRange === "custom" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: 12, color: TEXT_MUTED }}>From</span>
                  <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={dateInpSt} />
                  <span style={{ fontSize: 12, color: TEXT_MUTED }}>to</span>
                  <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={dateInpSt} />
                </Box>
              )}
            </Box>

            {/* Table header */}
            <Box sx={{
              display: "grid", gridTemplateColumns: gridCols, alignItems: "center",
              p: "11px 20px", background: "#FAFAF8", borderBottom: `1px solid ${BORDER_LIGHT}`,
              fontSize: 10.5, fontWeight: 700, letterSpacing: ".7px", textTransform: "uppercase",
              color: TEXT_FAINT, flexShrink: 0,
            }}>
              <Box>Client</Box>
              <Box>Well</Box>
              <Box>Description</Box>
              <Box>Date</Box>
              <Box>Created by</Box>
              <Box sx={{ textAlign: "right" }}>Actions</Box>
            </Box>

            {/* Table rows */}
            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {isLoading && tableData.length === 0 && (
                <Box sx={{ p: "48px 20px", textAlign: "center", color: TEXT_FAINT, fontSize: 13 }}>Loading…</Box>
              )}
              {!isLoading && tableData.length === 0 && (
                <Box sx={{ p: "56px 20px", textAlign: "center", color: TEXT_FAINT }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: "12px", background: "#F4F4F0", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: "14px", color: "#C2C6CE" }}>
                    <DocSvg />
                  </Box>
                  <Box sx={{ fontSize: 14, fontWeight: 600, color: "#6B7280" }}>No proposals match your filters</Box>
                  <Box sx={{ fontSize: 13, mt: "4px" }}>Try clearing a filter or selecting a different well.</Box>
                </Box>
              )}
              {tableData.map((item: any) => (
                <Box key={item.id} sx={{
                  display: "grid", gridTemplateColumns: gridCols, alignItems: "center",
                  p: "10px 20px", borderBottom: `1px solid ${BORDER_GRID}`, fontSize: 13.5,
                  "&:hover": { background: "#FBFAF7" },
                }}>
                  <Box sx={{ fontWeight: 600, color: OG, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: "10px" }}>
                    <Box component={Link} to={`/tech/${item.id}`} target="_blank" rel="noopener noreferrer"
                      sx={{ color: OG, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                      {item.well?.client?.name ?? "—"}
                    </Box>
                  </Box>
                  <Box sx={{ color: TEXT_MID, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: "8px" }}>
                    {item.well?.name ?? "—"}
                  </Box>
                  <Box sx={{ color: TEXT_MID, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: "8px" }}>
                    {item.customName ?? "—"}
                  </Box>
                  <Box sx={{ color: TEXT_MID, whiteSpace: "nowrap" }}>{fmtDate(item.date ?? "")}</Box>
                  <Box sx={{ color: TEXT_MID, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: "8px" }}>
                    {item.createdBy ?? "—"}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "4px", flexWrap: "nowrap" }}>
                    <ActionBtn title="Edit" onClick={() => navigate(`/tech/${item.id}`)}>
                      <EditSvg />
                    </ActionBtn>
                    {canManageDesigns && (
                      <ActionBtn title="Duplicate" onClick={() => { fetchDataTechProp(item.id); navigate("/tech/0"); }}>
                        <CopySvg />
                      </ActionBtn>
                    )}
                    {canManageDesigns && (
                      <ActionBtn title="Create as Tally" onClick={() => { fetchDataTechProp(item.id, true); navigate("/tally/0"); }}>
                        <ConvertSvg />
                      </ActionBtn>
                    )}
                    {canManageDesigns && (
                      <ActionBtn title="Generate Sales" onClick={() => navigate(`/sales/tech/${item.id}`)}>
                        <SalesSvg />
                      </ActionBtn>
                    )}
                    <Tooltip title="Print" arrow>
                      <Box component={Link} to={`/tech/${item.id}?pdf=true`}
                        sx={{
                          width: 28, height: 28, borderRadius: 7, border: "1px solid #E6E6E1",
                          background: CARD, color: "#6B7280", display: "inline-flex",
                          alignItems: "center", justifyContent: "center", cursor: "pointer",
                          textDecoration: "none", flexShrink: 0,
                          "&:hover": { borderColor: OG, color: OG, background: "#FDF6EC" },
                        }}>
                        <PrintSvg />
                      </Box>
                    </Tooltip>
                    {canManageDesigns && (
                      <ActionBtn title="Delete" danger onClick={() => { setIsOpenAlert(true); setIdToDelete(item.id); }}>
                        <TrashSvg />
                      </ActionBtn>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Pagination footer */}
            <Box sx={{
              p: "11px 20px", borderTop: `1px solid ${BORDER_LIGHT}`,
              display: "flex", alignItems: "center", gap: "16px",
              fontSize: 12.5, color: "#7A808C", flexShrink: 0,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>Rows per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => fetchPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })}
                  style={{ padding: "5px 8px", border: "1px solid #E2E2DD", borderRadius: 7, fontSize: 12.5, background: SEARCH_BG, outline: "none", cursor: "pointer", fontFamily: "inherit" }}
                >
                  <option value={8}>8</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </Box>
              <Box sx={{ ml: "auto" }}>{pageStart}–{pageEnd} of {total}</Box>
              <Box sx={{ display: "flex", gap: "6px" }}>
                <Box component="button" onClick={() => curPage > 0 && fetchPaginationModel({ page: curPage - 1, pageSize })}
                  disabled={curPage === 0} style={curPage > 0 ? navBtnSt : navBtnOffSt}>
                  <ChevronLeftSvg />
                </Box>
                <Box component="button" onClick={() => curPage < totalPages - 1 && fetchPaginationModel({ page: curPage + 1, pageSize })}
                  disabled={curPage >= totalPages - 1} style={curPage < totalPages - 1 ? navBtnSt : navBtnOffSt}>
                  <ChevronRightSvg />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this Technical Design?</div>}
          onAccept={() => { onDeleteProp(idToDelete); setIdToDelete(""); setIsOpenAlert(false); fetchData(); }}
          onCancel={() => { setIsOpenAlert(false); setIdToDelete(""); }}
        />
      )}
    </>
  );
};

// ── Reusable action button ──
const ActionBtn = ({
  children, title, onClick, danger = false,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  danger?: boolean;
}) => (
  <Tooltip title={title} arrow>
    <Box component="button" onClick={onClick}
      sx={{
        width: 28, height: 28, borderRadius: 7, border: "1px solid #E6E6E1",
        background: CARD, color: "#6B7280", display: "inline-flex",
        alignItems: "center", justifyContent: "center", cursor: "pointer",
        padding: 0, flexShrink: 0,
        "&:hover": danger
          ? { borderColor: "#DC2626", color: "#DC2626", background: "#FEF2F2" }
          : { borderColor: OG, color: OG, background: "#FDF6EC" },
      }}>
      {children}
    </Box>
  </Tooltip>
);

// ── SVG icons ──
const SearchSvg = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx={11} cy={11} r={7} /><line x1={21} y1={21} x2={16.65} y2={16.65} />
  </svg>
);
const CheckSvg = () => (
  <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const CloseSvg = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
    <line x1={18} y1={6} x2={6} y2={18} /><line x1={6} y1={6} x2={18} y2={18} />
  </svg>
);
const EditSvg = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const CopySvg = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x={9} y={9} width={13} height={13} rx={2} ry={2}/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);
const ConvertSvg = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);
const SalesSvg = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1={7} y1={7} x2={7.01} y2={7}/>
  </svg>
);
const PrintSvg = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x={6} y={14} width={12} height={8}/>
  </svg>
);
const TrashSvg = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const DocSvg = () => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v5h5"/><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
  </svg>
);
const ChevronLeftSvg = () => (
  <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const ChevronRightSvg = () => (
  <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
